import {
  CheckCircleIcon,
  ClockIcon,
  DeviceMobileCameraIcon,
  QrCodeIcon,
} from "@phosphor-icons/react";
import { useList, useTranslate } from "@refinedev/core";
import { Alert, Button, Modal, Space, Spin } from "antd";
import useGetTransferToken from "hooks/useGetTransferToken";
import { useOrganization } from "hooks/useOrganization";
import { ContactResponse, VoiceProfileResponse } from "pages/media/types";
import type QRCodeType from "qrcode";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { radius } from "@libertify/ui";

interface QRCodeDisplayProps {
  contactId?: string;
  voiceOnly?: boolean;
  baseUrl?: string;
  size?: number;
  showButton?: boolean;
  language?: string;
  onComplete?: () => void;
}

type QRStatus = "idle" | "generated" | "opened" | "completed" | "error";

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  contactId,
  baseUrl = globalThis.location.origin,
  voiceOnly,
  size = 200,
  showButton = true,
  language = "en",
  onComplete,
}) => {
  const t = useTranslate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrStatus, setQrStatus] = useState<QRStatus>("idle");
  const { organization } = useOrganization({});
  const { refetch: fetchTransferToken } = useGetTransferToken({
    enabled: false,
  });
  const [initialContactCount, setInitialContactCount] = useState<number | null>(
    null
  );
  const [initialVoiceProfileCount, setInitialVoiceProfileCount] = useState<
    number | null
  >(null);

  // Polling for contacts to detect when a new contact is created
  const { data: contactsData } = useList<ContactResponse>({
    resource: `media/${organization?.id}/contacts`,
    pagination: { mode: "off" },
    queryOptions: {
      enabled: !!organization?.id && !voiceOnly && isModalVisible,
      refetchInterval:
        isModalVisible &&
        initialContactCount !== null &&
        qrStatus !== "completed"
          ? 2000
          : false,
    },
  });

  const StatusAlert = () => {
    const statusDisplay = getStatusDisplay();
    if (!statusDisplay) return null;

    return (
      <Alert
        message={
          <Space>
            {statusDisplay.showSpinner && <Spin size="small" />}
            {statusDisplay.icon}
            <span>{statusDisplay.message}</span>
          </Space>
        }
        type={statusDisplay.type}
        showIcon={false}
        style={{ textAlign: "left" }}
      />
    );
  };

  // Store the initial contact count when modal opens and polling starts
  useEffect(() => {
    if (isModalVisible && contactsData?.data && initialContactCount === null) {
      setInitialContactCount(contactsData.data.length);
    }
  }, [isModalVisible, contactsData?.data, initialContactCount]);

  // Check if a new contact was created (contact count increased)
  useEffect(() => {
    if (
      initialContactCount !== null &&
      contactsData?.data &&
      contactsData.data.length > initialContactCount &&
      qrStatus !== "completed"
    ) {
      // New contact detected, update status and close modal
      setQrStatus("completed");
      // Close modal after a brief delay so user sees the success state
      setTimeout(() => {
        setIsModalVisible(false);
        setQrStatus("idle");
        setInitialContactCount(null);
        onComplete?.();
      }, 1500);
    }
  }, [contactsData?.data, initialContactCount, qrStatus, onComplete]);

  // Polling for voice profiles to detect when a new voice profile is created (voiceOnly mode)
  const { data: voiceProfilesData } = useList<VoiceProfileResponse>({
    resource: `media/${organization?.id}/voice_profiles`,
    pagination: { mode: "off" },
    filters: [
      {
        field: "tags",
        operator: "eq",
        value: JSON.stringify({ language: [language] }),
      },
    ],
    queryOptions: {
      enabled: !!organization?.id && voiceOnly && isModalVisible,
      refetchInterval:
        isModalVisible &&
        voiceOnly &&
        initialVoiceProfileCount !== null &&
        qrStatus !== "completed"
          ? 2000
          : false,
    },
  });

  // Store the initial voice profile count when modal opens and polling starts
  useEffect(() => {
    if (
      isModalVisible &&
      voiceOnly &&
      voiceProfilesData?.data &&
      initialVoiceProfileCount === null
    ) {
      setInitialVoiceProfileCount(voiceProfilesData.data.length);
    }
  }, [
    isModalVisible,
    voiceOnly,
    voiceProfilesData?.data,
    initialVoiceProfileCount,
  ]);

  // Check if a new voice profile was created (voice profile count increased)
  useEffect(() => {
    if (
      voiceOnly &&
      initialVoiceProfileCount !== null &&
      voiceProfilesData?.data &&
      voiceProfilesData.data.length > initialVoiceProfileCount &&
      qrStatus !== "completed"
    ) {
      // New voice profile detected, update status and close modal
      setQrStatus("completed");
      // Close modal after a brief delay so user sees the success state
      setTimeout(() => {
        setIsModalVisible(false);
        setQrStatus("idle");
        setInitialVoiceProfileCount(null);
        onComplete?.();
      }, 1500);
    }
  }, [
    voiceOnly,
    voiceProfilesData?.data,
    initialVoiceProfileCount,
    qrStatus,
    onComplete,
  ]);

  const generateQRCode = useCallback(async () => {
    try {
      // Fetch transfer token on demand
      const { data: tokenData } = await fetchTransferToken();
      setIsGenerating(true);
      setQrStatus("idle");

      // IMPORT DYNAMICALLY QRCODE LIB
      const QRCode: typeof QRCodeType = (await import("qrcode")).default;

      // Create URL with short token instead of full access token
      const qrCodeUrl = new URL("/media/contacts/clone/mobile", baseUrl);
      qrCodeUrl.searchParams.set("qr_id", tokenData.data.transfer_token);

      if (organization) {
        qrCodeUrl.searchParams.set("organizationId", organization.id);
      }

      if (voiceOnly) {
        qrCodeUrl.searchParams.set("voiceOnly", "true");
      }

      // Generate QR code
      const canvas = canvasRef.current;
      if (canvas) {
        await QRCode.toCanvas(canvas, qrCodeUrl.toString(), {
          width: size,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
      }

      // Also generate as data URL for modal display
      const dataUrl = await QRCode.toDataURL(qrCodeUrl.toString(), {
        width: size,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      setQrCodeDataUrl(dataUrl);
      setQrStatus("generated");
    } catch (error) {
      console.error("Error generating QR code:", error);
      setQrStatus("error");
    } finally {
      setIsGenerating(false);
    }
  }, [fetchTransferToken, baseUrl, organization, voiceOnly, size]);

  useEffect(() => {
    if (isModalVisible) {
      generateQRCode();
    }
  }, [isModalVisible, generateQRCode]);

  const handleShowQR = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setQrStatus("idle");
    setInitialContactCount(null);
    setInitialVoiceProfileCount(null);
  };

  const getStatusDisplay = () => {
    switch (qrStatus) {
      case "idle":
        return {
          icon: <QrCodeIcon size={20} />,
          message: t("components.QRCodeDisplay.scan"),
          type: "info" as const,
          showSpinner: false,
        };
      case "generated":
        return {
          icon: <ClockIcon size={20} />,
          message: t("components.QRCodeDisplay.waiting"),
          type: "info" as const,
          showSpinner: true,
        };
      case "opened":
        return {
          icon: <CheckCircleIcon size={20} />,
          message: t("components.QRCodeDisplay.scanned"),
          type: "success" as const,
          showSpinner: true,
        };
      case "completed":
        return {
          icon: <CheckCircleIcon size={20} />,
          message: t("components.QRCodeDisplay.completed"),
          type: "success" as const,
          showSpinner: false,
        };
      case "error":
        return {
          icon: <QrCodeIcon size={20} />,
          message: t("components.QRCodeDisplay.error"),
          type: "error" as const,
          showSpinner: false,
        };
      default:
        return null;
    }
  };

  if (!showButton) {
    return (
      <div style={{ textAlign: "center" }}>
        <canvas ref={canvasRef} style={{ display: "none" }} />
        {isGenerating ? (
          <Spin size="large" />
        ) : (
          <img
            src={qrCodeDataUrl}
            alt="QR Code"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <Button
        type="primary"
        icon={
          <DeviceMobileCameraIcon
            size={20}
            style={{ verticalAlign: "text-top" }}
          />
        }
        onClick={handleShowQR}
        loading={isGenerating}
        style={{ marginTop: 16 }}
      >
        {t("components.QRCodeDisplay.show")}
      </Button>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      <Modal
        title={
          <Space>
            <QrCodeIcon size={20} />
            <span>{t("components.QRCodeDisplay.title")}</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseModal}>
            {t("components.QRCodeDisplay.close")}
          </Button>,
        ]}
        width={400}
        centered
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          {isGenerating ? (
            <Spin size="large" />
          ) : (
            <>
              <img
                src={qrCodeDataUrl}
                alt="QR Code"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: radius.box,
                  padding: "16px",
                  backgroundColor: "#fff",
                }}
              />

              {qrStatus !== "idle" && (
                <div style={{ marginTop: 16 }}>
                  <StatusAlert />
                </div>
              )}

              {qrStatus === "idle" && (
                <div style={{ marginTop: 16 }}>
                  <Alert
                    message={
                      <Space>
                        <QrCodeIcon size={20} />
                        <span>{t("components.QRCodeDisplay.scan")}</span>
                      </Space>
                    }
                    type="info"
                    showIcon={false}
                    style={{ textAlign: "left" }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default QRCodeDisplay;
